import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * The venue's invoicing-provider connection (SmartBill etc.) — the account its booking
 * invoices are issued on. Mirrors InvoicingSettingsDto: credentials are WRITE-ONLY, the
 * server only ever says whether a connection exists, never what the token is.
 */
/**
 * One credential input a provider needs — the adapter self-describes them, so the connect
 * form renders whatever the backend advertises and a new provider needs no UI changes.
 */
export interface InvoicingCredentialField {
  key: string;
  isSecret: boolean;
}

export interface InvoicingProviderOption {
  code: string;
  credentialFields: InvoicingCredentialField[];
  /** The vendor issues nothing without a document series, so the form must demand one. */
  requiresSeriesName: boolean;
}

/**
 * One placeholder a line accepts, with the value to substitute when previewing it. The sample
 * comes from the server so a preview cannot drift from what a real document would say.
 */
export interface InvoiceLineToken {
  name: string;
  sample: string;
}

/**
 * One rewordable line on the venue's own documents. Self-described — its placeholders travel
 * with it, so the editor and its live preview are rendered from data and a line added on the
 * backend needs no change here.
 */
export interface InvoiceLineTemplate {
  /**
   * 'booking' | 'bookingRefund' | 'cancellationFee' | 'paidAmenity' | 'eventEntry' | 'eventRefund'
   * | 'classEntry' | 'classRefund' | 'tournamentEntry' | 'tournamentRefund'
   */
  key: string;
  /** Section of the card: 'bookings' or 'events'. Optional so an older backend still renders. */
  group?: string;
  /** For a storno line, the key of the line it reverses — it is shown nested under that line. */
  reversalOf?: string | null;
  /** The venue's own wording, or null while the line tracks the platform default. */
  template: string | null;
  defaultTemplate: string;
  /** The effective template rendered with the sample values — what the line actually says. */
  sample: string;
  tokens: InvoiceLineToken[];
}

export interface InvoicingSettings {
  isConnected: boolean;
  providerCode: string | null;
  seriesName: string | null;
  isEnabled: boolean;
  /** Whether this venue issues a nominal invoice to individual players (see the toggle). */
  invoiceIndividuals: boolean;
  /** Whether the player app offers "invoice my company" at this venue's checkout. */
  offerCompanyInvoice: boolean;
  lastValidatedAt: string | null;
  lastError: string | null;
  availableProviders: InvoicingProviderOption[];
  /** How this venue words each line on its documents. */
  lineTemplates: InvoiceLineTemplate[];
  /**
   * True when nothing is connected but documents are still issued — the platform default,
   * Stripe Invoicing on the venue's own connected account. Optional so an older backend
   * (field absent) reads as "no default", never as broken.
   */
  usesStripeFallback?: boolean;
}

export interface ConnectInvoicingRequest {
  providerCode: string;
  credentials: Record<string, string>;
  seriesName: string | null;
}

/**
 * The key-ring test value, decrypted server-side. If the GET fails with
 * KEY_RING_PROBE_UNREADABLE after a redeploy, the Data Protection key ring did not survive —
 * which is exactly what the test exists to reveal before real venue credentials are stranded.
 */
export interface KeyRingProbe {
  value: string;
  storedAt: string | null;
}

/**
 * `IssuedExternally` means somebody raised the document by hand at the provider and recorded
 * its number here, so the platform stops trying to issue a second one for the same thing.
 */
export type InvoiceStatus = 'Pending' | 'Issued' | 'Failed' | 'Cancelled' | 'IssuedExternally';
/**
 * Booking/BookingRefund = the venue's own documents; the rest are platform→venue invoices.
 * `ManualSubscriptionPayment` is a subscription paid by cash or transfer — the same document as
 * `Subscription`, raised per recorded payment instead of per Stripe charge.
 */
export type InvoiceType =
  | 'Booking' | 'BookingRefund' | 'Subscription' | 'ManualSubscriptionPayment' | 'CreditPurchase';

/** One line of a commission invoice's per-booking justification (the "desfășurător"). */
/** One of the venue's own fiscal documents (a booking invoice or its storno). */
export interface InvoiceListItem {
  id: string;
  status: InvoiceStatus;
  type: InvoiceType;
  createdAt: string;
  issuedAt: string | null;
  series: string | null;
  number: string | null;
  buyerName: string | null;
  /** The reservation number the document is for. */
  reference: string | null;
  /** Document total as invoiced; negative for a storno. */
  amount: number;
  currency: string | null;
  lastError: string | null;
  /**
   * Issued through the platform by a provider that exposes a PDF (Stripe, SmartBill) — drives
   * the download action. Optional so an older backend (field absent) reads as "no PDF".
   */
  hasPdf?: boolean;
}

export interface PaginatedInvoices {
  items: InvoiceListItem[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

@Injectable({ providedIn: 'root' })
export class InvoicingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/admin/my-complex/invoicing`;

  getSettings(): Observable<InvoicingSettings> {
    return this.http.get<InvoicingSettings>(this.baseUrl);
  }

  /** Verifies the credentials with a live provider call server-side before storing. */
  connect(request: ConnectInvoicingRequest): Observable<InvoicingSettings> {
    return this.http.put<InvoicingSettings>(this.baseUrl, request);
  }

  testConnection(): Observable<InvoicingSettings> {
    return this.http.post<InvoicingSettings>(`${this.baseUrl}/test`, {});
  }

  /** Changes only the series — verified with the provider before it is stored. */
  setSeries(seriesName: string | null): Observable<InvoicingSettings> {
    return this.http.put<InvoicingSettings>(`${this.baseUrl}/series`, { seriesName });
  }

  /**
   * Rewords one line on the venue's documents. A null template resets that line to the platform
   * default. The server re-checks the placeholders before storing — these end up on a fiscal
   * document, so the browser is not the authority on what reaches one.
   */
  setLineTemplate(key: string, template: string | null): Observable<InvoicingSettings> {
    return this.http.put<InvoicingSettings>(`${this.baseUrl}/line-template`, { key, template });
  }

  setEnabled(isEnabled: boolean): Observable<InvoicingSettings> {
    return this.http.put<InvoicingSettings>(`${this.baseUrl}/enabled`, { isEnabled });
  }

  /**
   * Turn nominal invoicing for individual players on or off. Its own endpoint, not a field on
   * the connection: pausing the integration must not reset a fiscal policy the venue chose.
   */
  setInvoiceIndividuals(invoiceIndividuals: boolean): Observable<InvoicingSettings> {
    return this.http.put<InvoicingSettings>(
      `${this.baseUrl}/invoice-individuals`, { invoiceIndividuals });
  }

  /**
   * Offer, or stop offering, "invoice my company" in the player app. Its own endpoint for the
   * same reason as the one above, and enforced server-side too — an installed app that still
   * draws the field cannot get past it.
   */
  setOfferCompanyInvoice(offerCompanyInvoice: boolean): Observable<InvoicingSettings> {
    return this.http.put<InvoicingSettings>(
      `${this.baseUrl}/offer-company-invoice`, { offerCompanyInvoice });
  }

  disconnect(): Observable<void> {
    return this.http.delete<void>(this.baseUrl);
  }

  /** Encrypts and stores the value through the venues' credential-protection chain. */
  setKeyRingProbe(value: string): Observable<boolean> {
    return this.http.put<boolean>(`${this.baseUrl}/key-test`, { value });
  }

  getKeyRingProbe(): Observable<KeyRingProbe> {
    return this.http.get<KeyRingProbe>(`${this.baseUrl}/key-test`);
  }

  /** Status narrows server-side, so the table's filter stays truthful across pages. */
  getInvoices(page: number, status?: InvoiceStatus, pageSize = 10): Observable<PaginatedInvoices> {
    return this.http.get<PaginatedInvoices>(
      `${this.baseUrl}/invoices`,
      { params: { page, pageSize, ...(status ? { status } : {}) } });
  }

  /** The document's PDF, fetched server-side from the provider that issued it. */
  downloadInvoicePdf(issuanceId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/invoices/${issuanceId}/pdf`, { responseType: 'blob' });
  }

  /** The invoices the platform issued TO this venue (subscriptions, credits, commissions). */
  getPlatformInvoices(page: number, pageSize = 10): Observable<PaginatedInvoices> {
    return this.http.get<PaginatedInvoices>(
      `${this.baseUrl}/platform-invoices`, { params: { page, pageSize } });
  }

  /**
   * Record a document that was issued by hand at the provider. The row leaves the queue for
   * good — this is the guard against ending up with two invoices for one booking.
   */
  markIssuedExternally(
    issuanceId: string, body: { number: string; series?: string | null; issuedOn?: string | null },
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/invoices/${issuanceId}/issued-externally`, body);
  }
}
